import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
  Row,
  Collapse,
} from "reactstrap";
import { css } from "react-emotion";
import { Formik, ErrorMessage } from "formik";
import swal from "sweetalert2";

import { getBase64 } from "@/util/fileToBase64";
import { handleError } from "@/util/errorHandler";
import merchants from "@/api/merchants";
import UploadButton from "../Components/KeyRecoveryRequest/UploadButton";
import MapContainer from "../Components/MapContainer";
import LoadingOverlay from "../Components/LoadingOverlay";
import { MAX_FILE_SIZE_IN_BYTES } from "@/store/constants";
import InputMask from "react-input-mask";
import {
  isPhoneNumber,
  clearPhoneNumber,
  facebookRegExp,
  trimField,
} from "@/util/validationHelper";
import moment from "moment";
import "rc-time-picker/assets/index.css";
import TimePicker from "rc-time-picker";

const format = "h:mm A";
const timeObjectToMoment = t => {
  return t
    ? moment()
        .hour(t.hour)
        .minute(t.minute)
    : null;
};
const defaultTimeValue = timeObjectToMoment({ hour: 12, minute: 0 });

const modalStyles = css`
  overflow: visible;
  width: 35rem;
  margin: 2.5% auto;

  .buttons {
    display: flex;
    justify-content: space-between;
    margin: 15px 0 0;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75) !important;
  z-index: 1021 !important; /* above navs, below sweetalert */
`;

const DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const DEFAULT_SCHEDULE = {
  MONDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  TUESDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  WEDNESDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  THURSDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  FRIDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  SATURDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
  SUNDAY: {
    from: null,
    to: null,
    lunchTimeFrom: null,
    lunchTimeTo: null,
  },
};

class UpdateMerchantModal extends React.Component {
  state = {
    logoFile: null,
    promoFile: null,
    open24: false,
    scheduleOptionsIsOpen: false,
    workDays: { ...DEFAULT_SCHEDULE },
    isLoading: false,
  };

  baseState = {
    ...this.state,
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.data.workSchedule &&
      this.props.data.workSchedule !== prevProps.data.workSchedule
    ) {
      let schedule = this.props.data.workSchedule;
      const open24 = schedule.open24;

      const workDays = { ...DEFAULT_SCHEDULE };

      for (let {
        dayOfWeek,
        from,
        to,
        lunchTimeFrom,
        lunchTimeTo,
      } of schedule.workDays) {
        workDays[dayOfWeek] = {
          from: null,
          to: null,
          lunchTimeFrom: null,
          lunchTimeTo: null,
        };
        if (from) workDays[dayOfWeek].from = { ...from };
        if (to) workDays[dayOfWeek].to = { ...to };
        if (lunchTimeFrom)
          workDays[dayOfWeek].lunchTimeFrom = { ...lunchTimeFrom };
        if (lunchTimeTo) workDays[dayOfWeek].lunchTimeTo = { ...lunchTimeTo };
      }

      this.setState({
        open24,
        workDays,
      });
    }
  }

  toggleScheduleOptions = () => {
    this.setState(({ scheduleOptionsIsOpen }) => ({
      scheduleOptionsIsOpen: !scheduleOptionsIsOpen,
    }));
  };

  validateFormik = ({
    name,
    type,
    lat,
    lng,
    address,
    phoneNumber,
    facebook,
    website,
  }) => {
    const errors = {};

    if (!name) {
      errors.name = "Please enter merchant's name";
    }

    if (!type) {
      errors.type = "Please enter merchant's type";
    }

    if (!lat) {
      errors.lat = "Please enter latitude";
    }

    if (!lng) {
      errors.lng = "Please enter longitude";
    }

    if (!address) {
      errors.address = "Please enter merchant's address";
    }

    if (phoneNumber && phoneNumber.length && !isPhoneNumber(phoneNumber)) {
      errors.phoneNumber = "Incorrect phone number";
    }
    if (phoneNumber && phoneNumber[5] === "0") {
      errors.phoneNumber = "Phone number can’t start with 0";
    }

    const checkFacebook = new RegExp(facebookRegExp).test(facebook);
    if (facebook && facebook.length && !checkFacebook) {
      errors.facebook = "Incorrect facebook address";
    }

    if (
      website &&
      website.length &&
      !/^(https?:\/\/)?(www\.)?([a-zA-Z0-9]+(-?[a-zA-Z0-9])*\.)+[\w]{2,}(\/\S*)?$/g.test(
        website,
      )
    ) {
      errors.website = "Incorrect website address";
    }

    return errors;
  };

  onSelectLogo = file => {
    this.setState({ logoFile: file });
  };

  onSelectPromo = file => {
    this.setState({ promoFile: file });
  };

  onChangeOpen24 = event => {
    this.setState({ open24: event.target.checked });
  };

  onChangeTime = (day, isLunch, isStart, newTime) => {
    const timeObject = newTime
      ? {
          hour: newTime.hour(),
          minute: newTime.minute(),
        }
      : null;

    let newDay = { ...this.state.workDays[day] };

    if (isLunch) {
      if (isStart) {
        newDay.lunchTimeFrom = timeObject;
      } else {
        newDay.lunchTimeTo = timeObject;
      }
    } else {
      if (isStart) {
        newDay.from = timeObject;
      } else {
        newDay.to = timeObject;
      }
    }

    this.setState({
      workDays: {
        ...this.state.workDays,
        [day]: newDay,
      },
    });
  };

  prepareDataBeforeSubmit = async ({
    name,
    type,
    lat,
    lng,
    address,
    phoneNumber,
    website,
    facebook,
  }) => {
    const { logoFile, promoFile, open24, workDays } = this.state;
    const { addNew } = this.props;

    const logo = logoFile ? await getBase64(logoFile) : this.props.logoFile;
    const promoImage = promoFile
      ? await getBase64(promoFile)
      : this.props.promoFile;

    const workSchedule = {
      open24,
      workDays: DAYS.reduce((result, day) => {
        const daySchedule = {};

        if (workDays[day].from) {
          daySchedule.from = workDays[day].from;
        }
        if (workDays[day].to) {
          daySchedule.to = workDays[day].to;
        }
        if (workDays[day].lunchTimeFrom) {
          daySchedule.lunchTimeFrom = workDays[day].lunchTimeFrom;
        }
        if (workDays[day].lunchTimeTo) {
          daySchedule.lunchTimeTo = workDays[day].lunchTimeTo;
        }

        if (Object.keys(daySchedule).length === 0) {
          return result;
        }

        daySchedule.dayOfWeek = day;

        return [...result, daySchedule];
      }, []),
    };

    const data = {
      name,
      type,
      address,
      phoneNumber: phoneNumber ? clearPhoneNumber(phoneNumber) : undefined,
      workSchedule,
      website,
      facebook,
      coordinates: {
        lat,
        lon: lng,
      },
      logo,
      promoImage,
    };

    if (!addNew) {
      data.id = this.props.data.id;
    }
    return data;
  };

  onUpdateMerchant = async form => {
    this.setState({ isLoading: true });
    const { addNew } = this.props;
    const data = await this.prepareDataBeforeSubmit(form);
    try {
      await (addNew
        ? merchants.addMerchant(data)
        : merchants.updateMerchant(data));

      swal(
        "Success",
        `Merchant ${data.name} has been sent for synchronization. Usually, it takes 5-10 minutes. In case of synchronization issues, please check the status of the merchant creation request.`,
        "success",
      );
      this.beforeClose();
    } catch (error) {
      this.setState({ isLoading: false });
      handleError(error);
    }
  };

  reset = () => {
    this.setState(this.baseState);
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  isFromSet = day => {
    return !!this.state.workDays[day].from;
  };

  isToSet = day => {
    return !!this.state.workDays[day].to;
  };

  isLunchTimeFromSet = day => {
    return !!this.state.workDays[day].lunchTimeFrom;
  };

  isLunchTimeToSet = day => {
    return !!this.state.workDays[day].lunchTimeTo;
  };

  render() {
    const {
      name,
      type,
      coordinates,
      address,
      phoneNumber,
      website,
      facebook,
    } = this.props.data;
    const { lat, lng } = coordinates || { lat: undefined, lng: undefined };

    const form = (
      <Formik
        initialValues={{
          name,
          type: type || (this.props.types[0] && this.props.types[0].name),
          lat,
          lng,
          address,
          phoneNumber,
          website,
          facebook,
        }}
        validate={this.validateFormik}
        onChange={this.validateFormik}
        onSubmit={this.onUpdateMerchant}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          touched,
          setErrors,
          setValues,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <LoadingOverlay loading={this.state.isLoading} />
              <FormGroup row>
                <Label sm={3}>Name</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Enter merchants's name"
                    invalid={Boolean(errors.name && touched.name)}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage
                    name="name"
                    component={FormFeedback}
                    className="text-alert"
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Type</Label>
                <Col sm={9}>
                  <Input
                    type="select"
                    name="type"
                    placeholder="Enter merchant's type"
                    invalid={Boolean(errors.type && touched.type)}
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}>
                    {this.props.types.map(type => {
                      return (
                        <option value={type.name} key={type.id}>
                          {type.name}
                        </option>
                      );
                    })}
                  </Input>
                  <ErrorMessage
                    name="type"
                    component={FormFeedback}
                    className="text-alert"
                  />
                </Col>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Label sm={3}>Coordinates</Label>
                  <Col sm={4}>
                    <Input
                      type="number"
                      step="any"
                      name="lat"
                      placeholder="Latitude"
                      invalid={Boolean(errors.lat && touched.lat)}
                      value={values.lat}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage
                      name="lat"
                      component={FormFeedback}
                      className="text-alert"
                    />
                  </Col>
                  <Col sm={4}>
                    <Input
                      type="number"
                      step="any"
                      name="lng"
                      placeholder="Longitude"
                      invalid={Boolean(errors.lng && touched.lng)}
                      value={values.lng}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage
                      name="lng"
                      component={FormFeedback}
                      className="text-alert"
                    />
                  </Col>
                </Row>
                <Row
                  className="justify-content-center"
                  style={{
                    minHeight: "200px",
                    maxWidth: "100%",
                    position: "relative",
                    marginTop: "1rem",
                  }}>
                  <Col sm={12}>
                    <MapContainer
                      coordinates={{ lat: values.lat, lng: values.lng }}
                      name={values.name}
                      canAddMarker
                      setCoords={(lat, lng) =>
                        setValues({ ...values, lat, lng })
                      }
                    />
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Address</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="address"
                    placeholder="Enter merchant's address"
                    invalid={Boolean(errors.address && touched.address)}
                    value={values.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.address && (
                    <FormFeedback className="text-alert">
                      {errors.address}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Phone number</Label>
                <Col sm={9}>
                  <InputMask
                    name="phoneNumber"
                    mask="+855 99 999 9999"
                    maskChar=""
                    value={values.phoneNumber}
                    onChange={handleChange}>
                    {inputProps => (
                      <Input
                        {...inputProps}
                        placeholder="Enter merchant's phone"
                        type="text"
                        autoComplete="off"
                        disableUnderline
                        invalid={Boolean(
                          errors.phoneNumber && touched.phoneNumber,
                        )}
                      />
                    )}
                  </InputMask>
                  {errors.phoneNumber && (
                    <FormFeedback className="text-alert">
                      {errors.phoneNumber}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Website</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="website"
                    placeholder="Enter merchant's website"
                    invalid={Boolean(errors.website && touched.website)}
                    value={values.website}
                    onChange={e => handleChange(trimField(e))}
                    onBlur={handleBlur}
                  />
                  {errors.website && (
                    <FormFeedback className="text-alert">
                      {errors.website}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Facebook</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="facebook"
                    placeholder="Enter merchant's facebook"
                    invalid={Boolean(errors.facebook && touched.facebook)}
                    value={values.facebook}
                    onChange={e => handleChange(trimField(e))}
                    onBlur={handleBlur}
                  />
                  {errors.facebook && (
                    <FormFeedback className="text-alert">
                      {errors.facebook}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Logo</Label>
                <Col sm={9}>
                  <UploadButton
                    width="100%"
                    defaultText="Attach logo"
                    onSelectFile={logoFile => {
                      setErrors({ ...errors, logoFile: undefined });
                      this.onSelectLogo(logoFile);
                    }}
                    accept="image/png,image/jpeg"
                    maxFileSize={MAX_FILE_SIZE_IN_BYTES}
                  />
                  {errors.logoFile && (
                    <FormFeedback className="text-alert">
                      {errors.logoFile}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Promo</Label>
                <Col sm={9}>
                  <UploadButton
                    width="100%"
                    defaultText="Attach promo"
                    onSelectFile={promoFile => {
                      setErrors({ ...errors, promoFile: undefined });
                      this.onSelectPromo(promoFile);
                    }}
                    accept="image/png,image/jpeg"
                    maxFileSize={MAX_FILE_SIZE_IN_BYTES}
                  />
                  {errors.promoFile && (
                    <FormFeedback className="text-alert">
                      {errors.promoFile}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Work schedule</Label>
                <Col sm={9}>
                  <Button onClick={this.toggleScheduleOptions}>
                    {this.state.scheduleOptionsIsOpen ? "Hide" : "Show"}
                  </Button>
                </Col>
              </FormGroup>
              <Collapse isOpen={this.state.scheduleOptionsIsOpen}>
                <FormGroup row>
                  <Label sm={3}>Open 24 hours</Label>
                  <Col sm={9}>
                    <Input
                      type="checkbox"
                      name="open24"
                      style={{
                        marginTop: "0.6rem",
                        marginLeft: 0,
                      }}
                      defaultChecked={this.state.open24}
                      onChange={this.onChangeOpen24}
                      onBlur={this.onChangeOpen24}
                    />
                  </Col>
                </FormGroup>
                {DAYS.map(day => (
                  <React.Fragment key={day}>
                    <FormGroup row>
                      <Label sm={3}>Work hours ({day})</Label>
                      <Col sm={4}>
                        From{" "}
                        <TimePicker
                          showSecond={false}
                          defaultValue={timeObjectToMoment(
                            this.state.workDays[day].from,
                          )}
                          defaultOpenValue={defaultTimeValue}
                          required={this.isToSet(day)}
                          onChange={value =>
                            this.onChangeTime(day, false, true, value)
                          }
                          format={format}
                          use12Hours
                          inputReadOnly
                        />
                      </Col>
                      <Col sm={4}>
                        To{" "}
                        <TimePicker
                          showSecond={false}
                          defaultValue={timeObjectToMoment(
                            this.state.workDays[day].to,
                          )}
                          defaultOpenValue={defaultTimeValue}
                          required={this.isFromSet(day)}
                          onChange={value =>
                            this.onChangeTime(day, false, false, value)
                          }
                          format={format}
                          use12Hours
                          inputReadOnly
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Label sm={3}>Lunch time ({day})</Label>
                      <Col sm={4}>
                        From{" "}
                        <TimePicker
                          showSecond={false}
                          defaultValue={timeObjectToMoment(
                            this.state.workDays[day].lunchTimeFrom,
                          )}
                          defaultOpenValue={defaultTimeValue}
                          required={this.isLunchTimeFromSet(day)}
                          onChange={value =>
                            this.onChangeTime(day, true, true, value)
                          }
                          format={format}
                          use12Hours
                          inputReadOnly
                        />
                      </Col>
                      <Col sm={4}>
                        To{" "}
                        <TimePicker
                          showSecond={false}
                          defaultValue={timeObjectToMoment(
                            this.state.workDays[day].lunchTimeTo,
                          )}
                          defaultOpenValue={defaultTimeValue}
                          required={this.isLunchTimeToSet(day)}
                          onChange={value =>
                            this.onChangeTime(day, true, false, value)
                          }
                          format={format}
                          use12Hours
                          inputReadOnly
                        />
                      </Col>
                    </FormGroup>
                  </React.Fragment>
                ))}
              </Collapse>
            </CardBody>
            <CardFooter>
              <div className="buttons">
                <Button
                  color={this.props.addNew ? "success" : "primary"}
                  type="submit">
                  <span>{this.props.addNew ? "Add" : "Update"}</span>
                </Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.beforeClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button close onClick={this.beforeClose} />

            <span>
              {this.props.addNew ? "Add new" : "Update"} merchant info
            </span>
          </CardHeader>
          {form}
        </Card>
      </Modal>
    );
  }
}

UpdateMerchantModal.defaultProps = {
  isOpen: false,
  addNew: false,
  onModalClose: () => {},
  data: {},
};

UpdateMerchantModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  addNew: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  types: PropTypes.array.isRequired,
  logoFile: PropTypes.string,
  promoFile: PropTypes.string,
};

export default UpdateMerchantModal;
